import React from 'react';
import { ArrowRightIcon } from '@heroicons/react/24/outline';

function Icon({ name, className }) {
  const iconClass = className || 'w-10 h-10';
  switch (name) {
    case 'growth':
      return (
        <svg className={iconClass} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M3 3v18h18" />
          <path d="M7 15l4-4 3 3 5-6" />
        </svg>
      );
    case 'succession':
      return (
        <svg className={iconClass} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M16 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
          <circle cx="10" cy="7" r="4" />
          <path d="M20 8l-3 3-3-3" />
        </svg>
      );
    case 'browse':
      return (
        <svg className={iconClass} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <circle cx="12" cy="12" r="3" />
          <path d="M2 12h3M19 12h3M12 2v3M12 19v3M4.22 4.22l2.12 2.12M17.66 17.66l2.12 2.12M17.66 6.34l2.12-2.12M4.22 19.78l2.12-2.12" />
        </svg>
      );
    default:
      return null;
  }
}

export default function StepMotivation({ value, onChange, onNext, fyiNote = null }) {
  const options = [
    { key: 'growth', label: 'Grow my Business', subtitle: 'Find partners, opportunities, and expansion paths', icon: 'growth' },
    { key: 'succession', label: 'Plan my Succession', subtitle: 'Prepare for transition and maximize outcomes', icon: 'succession' },
    { key: 'no_motivation', label: "I'm just browsing", subtitle: 'Explore the platform without commitments', icon: 'browse' },
  ];

  return (
    <div className="w-full px-8 py-12 sm:px-12 sm:py-16">
      <div className="w-full max-w-5xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl sm:text-5xl font-semibold text-gray-900 mb-4 tracking-tight">
            What brings you here?
          </h2>
          <p className="text-lg text-gray-500">
            We'll tailor your experience based on your selection
          </p>
        </div>

        {/* Options Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          {options.map((opt) => {
            const selected = value === opt.key;
            return (
              <button
                key={opt.key}
                onClick={() => onChange(opt.key)}
                className={`relative p-8 rounded-3xl transition-all duration-300 text-center group
                  ${
                    selected
                      ? 'text-white shadow-2xl scale-105'
                      : 'bg-white border-2 border-gray-200 hover:border-gray-300 hover:shadow-lg'
                  }`}
                style={selected ? {
                  backgroundColor: 'var(--succession-primary, var(--company-primary, #5489B6))'
                } : {}}
              >
                {/* Icon */}
                <div className="flex justify-center mb-6">
                  <div
                    className={`w-20 h-20 rounded-2xl flex items-center justify-center transition-all duration-300
                    ${
                      selected
                        ? 'bg-white/10 text-white'
                        : 'bg-gray-100 text-gray-600 group-hover:bg-gray-200'
                    }`}
                  >
                    <Icon name={opt.icon} className="w-10 h-10" />
                  </div>
                </div>

                {/* Content */}
                <div>
                  <h3
                    className={`text-xl font-semibold mb-3 transition-colors
                    ${selected ? 'text-white' : 'text-gray-900'}`}
                  >
                    {opt.label}
                  </h3>
                  <p
                    className={`text-sm leading-relaxed transition-colors
                    ${selected ? 'text-white/80' : 'text-gray-600'}`}
                  >
                    {opt.subtitle}
                  </p>
                </div>

                {/* Selection Indicator */}
                {selected && (
                  <div className="absolute top-4 right-4">
                    <div className="w-6 h-6 rounded-full bg-white flex items-center justify-center">
                      <div className="w-2 h-2 rounded-full" style={{ backgroundColor: 'var(--succession-primary, var(--company-primary, #5489B6))' }} />
                    </div>
                  </div>
                )}
              </button>
            );
          })}
        </div>

        {/* Next button */}
        <div className="flex justify-center">
          <button
            disabled={!value}
            onClick={onNext}
            className="inline-flex items-center gap-2 px-10 py-4 text-white font-medium text-lg disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-200 disabled:hover:opacity-30 group rounded-full"
            style={{
              backgroundColor: 'var(--succession-primary, var(--company-primary, #5489B6))'
            }}
            onMouseEnter={(e) => {
              if (!e.target.disabled) {
                e.target.style.opacity = '0.9';
              }
            }}
            onMouseLeave={(e) => {
              if (!e.target.disabled) {
                e.target.style.opacity = '1';
              }
            }}
          >
            Continue
            <ArrowRightIcon className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-200" />
          </button>
        </div>

        {/* Manager-approval FYI — mirrors the welcome-back screen; shown when the
            welcome-back flow + an approval workflow are enabled (passed by container). */}
        {fyiNote && (
          <div className="max-w-xl mx-auto mt-10 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3">
            <p className="text-sm font-semibold text-amber-900 text-center">{fyiNote}</p>
          </div>
        )}
      </div>
    </div>
  );
}


